
import { computed, defineComponent, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

import {
  convertRequestErrorToMap,
  notEmpty,
  Nullable,
  useResource,
  useToast,
} from "@tager/admin-services";
import {
  createTabErrorFinder,
  TabType,
  FormFooter,
  TagerFormSubmitEvent,
  FormField,
  FormFieldCheckbox,
  FormFieldComboBox,
} from "@tager/admin-ui";
import { Page } from "@tager/admin-layout";
import {
  DynamicField,
  FieldUnion,
  RepeaterIncomingValue,
  universalFieldUtils,
} from "@tager/admin-dynamic-field";

import { FullFieldType, FullProductTypeType } from "@/typings/model";
import {
  createField,
  getField,
  getProductType,
  updateField,
} from "@/services/requests";
import {
  convertFormValuesToFieldCreationPayload,
  convertFormValuesToFieldUpdatePayload,
  convertFieldToFormValues,
  FormValues,
  typeOptionList,
  convertSelectOptionListToRepeaterIncomingValue,
  selectFieldConfig,
  convertColorListToRepeaterIncomingValue,
  colorFieldConfig,
} from "@/views/FieldForm/FieldForm.helpers";
import {
  getFieldEditFormUrl,
  getFieldListUrl,
  getProductTypeListUrl,
} from "@/utils/paths";

export default defineComponent({
  name: "FieldForm",
  components: {
    Page,
    FormField,
    FormFieldCheckbox,
    FormFieldComboBox,
    DynamicField,
    FormFooter,
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const toast = useToast();

    const fieldId = computed<string>(() => route.params.fieldId as string);
    const productTypeId = computed<string>(
      () => route.params.productTypeId as string
    );

    const [
      fetchProductType,
      { data: productType, loading: isProductTypeLoading },
    ] = useResource<Nullable<FullProductTypeType>>({
      fetchResource: () => getProductType(productTypeId.value),
      initialValue: null,
      resourceName: "productType",
    });

    const isCreation = computed<boolean>(() => fieldId.value === "create");

    const [fetchField, { data: field, loading: isFieldLoading }] = useResource<
      Nullable<FullFieldType>
    >({
      fetchResource: () => getField(fieldId.value),
      initialValue: null,
      resourceName: "field",
    });

    onMounted(() => {
      if (isCreation.value) {
        fetchProductType();
        return;
      }
      fetchField();
    });

    const values = ref<FormValues>(convertFieldToFormValues(field.value));

    const errors = ref<Record<string, string>>({});

    const isSubmitting = ref<boolean>(false);

    const inputsSelectFieldIncomingValue = ref<RepeaterIncomingValue>();

    watch(field, () => {
      values.value = convertFieldToFormValues(field.value);
      inputsSelectFieldIncomingValue.value =
        convertSelectOptionListToRepeaterIncomingValue(values.value.options);
      updateInputSelectTableValues();
    });

    /** Select creation table */

    const inputsSelectField = ref<FieldUnion>(
      universalFieldUtils.createFormField(
        selectFieldConfig,
        inputsSelectFieldIncomingValue.value
      )
    );

    function updateInputSelectTableValues() {
      inputsSelectField.value = universalFieldUtils.createFormField(
        selectFieldConfig,
        inputsSelectFieldIncomingValue.value
      );
    }

    onMounted(() => {
      updateInputSelectTableValues();
    });

    watch([field], () => {
      updateInputSelectTableValues();
    });

    /** Color creation table */

    const inputsColorFieldIncomingValue = computed<RepeaterIncomingValue>(() =>
      convertColorListToRepeaterIncomingValue(values.value.options)
    );

    const inputsColorsField = ref<FieldUnion>(
      universalFieldUtils.createFormField(
        colorFieldConfig,
        inputsColorFieldIncomingValue.value
      )
    );

    function updateInputColorTableValues() {
      inputsColorsField.value = universalFieldUtils.createFormField(
        colorFieldConfig,
        inputsColorFieldIncomingValue.value
      );
    }

    onMounted(() => {
      updateInputColorTableValues();
    });

    watch([field], () => {
      updateInputColorTableValues();
    });

    /** Submit form */

    function submitForm(event: TagerFormSubmitEvent) {
      isSubmitting.value = true;

      const creationBody = convertFormValuesToFieldCreationPayload(
        values.value,
        productTypeId.value
      );
      const updateBody = convertFormValuesToFieldUpdatePayload(
        values.value,
        inputsSelectField.value,
        inputsColorsField.value
      );

      const requestPromise = isCreation.value
        ? createField(creationBody)
        : updateField(fieldId.value, updateBody);

      requestPromise
        .then((response) => {
          errors.value = {};

          if (event.type === "create") {
            router.push(getFieldEditFormUrl({ fieldId: response.data.id }));
          }
          if (event.type === "create_exit" || event.type === "save_exit") {
            router.push(
              getFieldListUrl({
                productTypeId: response.data.productTypeId,
              })
            );
          }
          if (event.type === "create_create-another") {
            values.value = convertFieldToFormValues(null);
          }

          toast.show({
            variant: "success",
            title: "Success",
            body: isCreation.value
              ? `Поле успешно добавлено`
              : "Поле успешно обновлено",
          });
        })
        .catch((error) => {
          console.error(error);
          errors.value = convertRequestErrorToMap(error);
          toast.show({
            variant: "danger",
            title: "Ошибка",
            body: isCreation.value
              ? `Ошибка при добавлении поля`
              : "Ошибка при обновлении поля",
          });
        })
        .finally(() => {
          isSubmitting.value = false;
        });
    }

    const isContentLoading = computed<boolean>(
      () => isFieldLoading.value || isProductTypeLoading.value
    );

    /** Tabs */

    const tabList = computed<Array<TabType>>(() => {
      const hasErrors = createTabErrorFinder(errors.value);

      function tabListArray() {
        if (values.value.type?.value === "TRUE_FALSE") {
          return [
            {
              id: "settings",
              label: "Настройки",
              hasErrors: hasErrors([]),
            },
          ];
        }
        if (values.value.type?.value === "COLOR") {
          return [
            {
              id: "colors",
              label: "Цвета",
              hasErrors: hasErrors([]),
            },
          ];
        }
        if (values.value.type?.value === "SELECT") {
          return [
            {
              id: "select",
              label: "Варианты",
              hasErrors: hasErrors([]),
            },
          ];
        }
        return [];
      }

      return [
        {
          id: "common",
          label: "Основное",
          hasErrors: hasErrors(["name", "systemName"]),
        },
      ]
        .concat(tabListArray())
        .filter(notEmpty);
    });

    const selectedTabId = ref<string>(tabList.value[0].id);

    const pageTitle = computed<string>(() => {
      return isCreation.value
        ? productType.value
          ? `Создание поля для товарного типа "${productType.value.name}"`
          : `Создание поля для товарного типа ""`
        : `Редактировать поле "${values.value.name}"`;
    });

    const backButtonUrl = computed<string>(() =>
      isCreation.value
        ? getFieldListUrl({
            productTypeId: productTypeId.value,
          })
        : field.value
        ? getFieldListUrl({
            productTypeId: field.value.productTypeId,
          })
        : getProductTypeListUrl()
    );
    return {
      backButtonUrl,
      values,
      errors,
      isSubmitting,
      submitForm,
      isContentLoading,
      field,
      isCreation,
      pageTitle,
      tabList,
      selectedTabId,
      typeOptionList,
      inputsSelectField,
      inputsColorsField,
    };
  },
});
